import React, { useId } from 'react';
import { formStyles } from '@utils/formStyles';

interface FormSwitchProps {
    id?: string;
    checked?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    /** Accessible name when no visible label is rendered, e.g. a switch inside a table cell. */
    ariaLabel?: string;
    disabled?: boolean;
    error?: string;
    helpText?: string;
    /** Drops the default bottom margin, for inline use in table cells and toolbars. */
    noMargin?: boolean;
    className?: string;
    labelClassName?: string;
}

const FormSwitch: React.FC<FormSwitchProps> = ({
    id,
    checked = false,
    onChange,
    label,
    ariaLabel,
    disabled = false,
    error,
    helpText,
    noMargin = false,
    className = '',
    labelClassName = ''
}) => {
    // Generate a stable ID if none provided
    const generatedId = useId();
    const switchId = id || generatedId;

    // Handle the toggle explicitly to ensure it works
    const handleToggle = () => {
        if (onChange && !disabled) {
            const event = {
                target: {
                    checked: !checked,
                    name: switchId,
                    id: switchId
                }
            } as React.ChangeEvent<HTMLInputElement>;

            onChange(event);
        }
    };

    return (
        <div className={`relative inline-block ${noMargin ? '' : 'mb-3'} ${className}`}>
            <div className="flex items-center">
                {/* Real hidden input for form submission */}
                <input
                    checked={checked}
                    disabled={disabled}
                    id={switchId}
                    style={{ position: 'absolute', opacity: 0 }}
                    type="checkbox"
                    onChange={onChange}
                />

                {/* Custom visual switch */}
                <div
                    aria-checked={checked}
                    aria-disabled={disabled}
                    aria-label={ariaLabel}
                    className={`relative inline-block w-10 h-5 rounded-full transition-colors duration-300 ${
                        checked ? 'bg-[#0088ff]' : 'bg-[#444]'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-[#0275d8] focus:ring-opacity-25`}
                    role="checkbox"
                    tabIndex={disabled ? -1 : 0}
                    onClick={!disabled ? handleToggle : undefined}
                    onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                            handleToggle();
                            e.preventDefault();
                        }
                    }}
                >
                    <span
                        className={`absolute block w-4 h-4 bg-white rounded-full left-0.5 bottom-0.5 transition-transform duration-300 ${
                            checked ? 'transform translate-x-5' : ''
                        }`}
                    />
                </div>

                {label && (
                    <label
                        className={`ml-2 cursor-pointer select-none align-middle m-0 ${labelClassName}`}
                        htmlFor={switchId}
                        onClick={!disabled ? handleToggle : undefined}
                    >
                        {label}
                    </label>
                )}
            </div>

            {helpText && <div className={formStyles.helpText}>{helpText}</div>}
            {error && <div className={formStyles.errorText}>{error}</div>}
        </div>
    );
};

export default FormSwitch;
